import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sidebar } from "./AdminDashboard";
import { toast } from "sonner";
import { 
  DollarSign, 
  Building2,
  FileText,
  Receipt,
  RefreshCw,
  Search,
  Calendar,
  ChevronRight,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Plus,
  Filter,
  Eye,
  Loader2,
  ArrowUpRight,
  Banknote,
  CloudDownload,
  History
} from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dashboard Tab Component
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue/dashboard`, {
        headers: getAdminAuthHeaders()
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch revenue dashboard:", error);
      toast.error("Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-emerald-500" data-testid="stat-total-volume">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Total Booking Volume</p>
            <p className="text-2xl font-bold text-midnight font-outfit">
              {formatCurrency(data?.summary?.total_booking_volume)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {data?.summary?.total_reservations || 0} reservations
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-bronze" data-testid="stat-total-fees">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-bronze" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Total Platform Fees (2%)</p>
            <p className="text-2xl font-bold text-midnight font-outfit">
              {formatCurrency(data?.summary?.total_platform_fees)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500" data-testid="stat-outstanding">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Banknote className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Outstanding Balance</p>
            <p className="text-2xl font-bold text-blue-600 font-outfit">
              {formatCurrency(data?.summary?.outstanding_balance)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Unbilled: {formatCurrency(data?.summary?.unbilled_fees)} | Invoiced: {formatCurrency(data?.summary?.invoiced_fees)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500" data-testid="stat-collected">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Collected (Paid)</p>
            <p className="text-2xl font-bold text-green-600 font-outfit">
              {formatCurrency(data?.summary?.paid_fees)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="recent-bookings-card">
          <CardHeader>
            <CardTitle className="font-outfit text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-bronze" />
              Recent Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.recent_bookings?.length > 0 ? (
              <div className="space-y-3">
                {data.recent_bookings.map((booking, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-midnight">{booking.guest_name}</p>
                      <p className="text-sm text-slate-500">{booking.property_name}</p>
                      <p className="text-xs text-slate-400">{formatDate(booking.synced_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatCurrency(booking.booking_total)}</p>
                      <p className="text-xs text-bronze">Fee: {formatCurrency(booking.platform_fee_amount)}</p>
                      <Badge className={`text-xs ${
                        booking.billing_status === 'paid' ? 'bg-green-100 text-green-700' :
                        booking.billing_status === 'invoiced' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {booking.billing_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No bookings yet</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="unpaid-invoices-card">
          <CardHeader>
            <CardTitle className="font-outfit text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Unpaid Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.unpaid_invoices?.length > 0 ? (
              <div className="space-y-3">
                {data.unpaid_invoices.map((invoice, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-midnight">{invoice.invoice_number}</p>
                      <p className="text-sm text-slate-500">{invoice.property_name}</p>
                      <p className="text-xs text-slate-400">Due: {formatDate(invoice.due_date)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">{formatCurrency(invoice.amount_due)}</p>
                      <p className="text-xs text-slate-500">{invoice.total_reservations} bookings</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No unpaid invoices</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Booking Ledger Tab Component
function LedgerTab() {
  const [ledger, setLedger] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    billing_status: "all",
    payment_source: "all",
    property_id: "all"
  });

  useEffect(() => {
    fetchLedger();
  }, [page, filters]);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.billing_status && filters.billing_status !== "all") params.append('billing_status', filters.billing_status);
      if (filters.payment_source && filters.payment_source !== "all") params.append('payment_source', filters.payment_source);
      if (filters.property_id && filters.property_id !== "all") params.append('property_id', filters.property_id);
      
      const response = await axios.get(`${API}/admin/revenue/ledger?${params}`, {
        headers: getAdminAuthHeaders()
      });
      setLedger(response.data.ledger);
      setTotals(response.data.totals);
      setTotalPages(Math.ceil(response.data.total / 20));
    } catch (error) {
      console.error("Failed to fetch ledger:", error);
      toast.error("Failed to load booking ledger");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      unbilled: 'bg-amber-100 text-amber-700',
      invoiced: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-700'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <label className="text-sm text-slate-600 mb-1 block">Billing Status</label>
              <Select 
                value={filters.billing_status} 
                onValueChange={(val) => setFilters({...filters, billing_status: val})}
              >
                <SelectTrigger data-testid="filter-billing-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="unbilled">Unbilled</SelectItem>
                  <SelectItem value="invoiced">Invoiced</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <label className="text-sm text-slate-600 mb-1 block">Payment Source</label>
              <Select 
                value={filters.payment_source} 
                onValueChange={(val) => setFilters({...filters, payment_source: val})}
              >
                <SelectTrigger data-testid="filter-payment-source">
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="woocommerce">WooCommerce</SelectItem>
                  <SelectItem value="cloudbeds_external">Cloudbeds (External)</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              onClick={() => { setFilters({ billing_status: "all", payment_source: "all", property_id: "all" }); setPage(1); }}
            >
              Clear Filters
            </Button>
            <Button variant="outline" onClick={fetchLedger}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-600">Filtered Bookings</p>
            <p className="text-xl font-bold text-midnight">{totals.count || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-600">Booking Volume</p>
            <p className="text-xl font-bold text-midnight">{formatCurrency(totals.booking_volume)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-600">Platform Fees (2%)</p>
            <p className="text-xl font-bold text-bronze">{formatCurrency(totals.platform_fees)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Ledger Table */}
      <Card data-testid="ledger-table">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-bronze" />
            </div>
          ) : ledger.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No booking entries found</p>
              <p className="text-sm">Bookings will appear here once synced through the plugin</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Property</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Guest</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Dates</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Fee (2%)</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((entry, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-midnight">{entry.property_name}</p>
                          <p className="text-xs text-slate-400">Res: {entry.reservation_id}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p>{entry.guest_name}</p>
                          <p className="text-xs text-slate-400">{entry.guest_email}</p>
                        </td>
                        <td className="py-3 px-4 text-xs">
                          <p>{entry.checkin_date} → {entry.checkout_date}</p>
                        </td>
                        <td className="py-3 px-4 font-medium">
                          {formatCurrency(entry.booking_total)}
                        </td>
                        <td className="py-3 px-4 font-semibold text-bronze">
                          {formatCurrency(entry.platform_fee_amount)}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(entry.billing_status)}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-500">
                          {formatDate(entry.synced_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Properties Tab Component
function PropertiesTab({ onSelectProperty }) {
  const [properties, setProperties] = useState([]);
  const [grandTotal, setGrandTotal] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/revenue/properties`, {
        headers: getAdminAuthHeaders()
      });
      setProperties(response.data.properties);
      setGrandTotal(response.data.grand_total);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Grand Totals */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-600">Total Properties</p>
            <p className="text-2xl font-bold text-midnight">{properties.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-600">Total Reservations</p>
            <p className="text-2xl font-bold text-midnight">{grandTotal.total_reservations || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-emerald-700">Total Booking Volume</p>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(grandTotal.total_booking_volume)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-amber-700">Total Outstanding</p>
            <p className="text-2xl font-bold text-amber-700">{formatCurrency(grandTotal.total_outstanding)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Properties List */}
      <Card data-testid="properties-list">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Building2 className="w-5 h-5 text-bronze" />
            Properties with Booking Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {properties.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No properties with bookings yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {properties.map((property, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => onSelectProperty(property)}
                >
                  <div className="flex-1">
                    <p className="font-semibold text-midnight text-lg">{property.property_name}</p>
                    <p className="text-sm text-slate-500">
                      {property.total_reservations} reservations · Volume: {formatCurrency(property.total_booking_volume)}
                    </p>
                    <code className="text-xs bg-slate-200 px-2 py-0.5 rounded mt-1 inline-block">
                      {property.license_key}
                    </code>
                  </div>
                  <div className="text-right mr-4">
                    <p className="text-sm text-slate-600">Outstanding</p>
                    <p className={`text-xl font-bold ${property.outstanding_balance > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {formatCurrency(property.outstanding_balance)}
                    </p>
                    <div className="flex gap-2 mt-1 text-xs">
                      <span className="text-amber-600">Unbilled: {formatCurrency(property.unbilled_fees)}</span>
                      <span className="text-blue-600">Invoiced: {formatCurrency(property.invoiced_fees)}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Invoices Tab Component
function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createData, setCreateData] = useState({ property_id: "", month: "", notes: "" });
  const [properties, setProperties] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchProperties();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/admin/revenue/invoices${params}`, {
        headers: getAdminAuthHeaders()
      });
      setInvoices(response.data.invoices);
      setSummary(response.data.summary);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue/properties`, {
        headers: getAdminAuthHeaders()
      });
      setProperties(response.data.properties);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    }
  };

  const createInvoice = async () => {
    if (!createData.property_id || !createData.month) {
      toast.error("Please select a property and month");
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/revenue/invoice/create`, createData, {
        headers: getAdminAuthHeaders()
      });
      toast.success(`Invoice ${response.data.invoice.invoice_number} created!`);
      setShowCreateModal(false);
      setCreateData({ property_id: "", month: "", notes: "" });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  const markAsPaid = async (invoiceId) => {
    try {
      await axios.post(`${API}/admin/revenue/invoice/${invoiceId}/mark-paid`, {}, {
        headers: getAdminAuthHeaders()
      });
      toast.success("Invoice marked as paid!");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to mark invoice as paid");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-600">Total Invoices</p>
            <p className="text-2xl font-bold text-midnight">{summary.total_invoices || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-red-600">Total Unpaid</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_unpaid)}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-green-600">Total Collected</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_paid)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions & Filter */}
      <div className="flex gap-4 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="filter-invoice-status">
            <SelectValue placeholder="All Invoices" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Invoices</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowCreateModal(true)} className="bg-bronze hover:bg-amber-600 text-white" data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
        <Button variant="outline" onClick={fetchInvoices}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Invoices Table */}
      <Card data-testid="invoices-table">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-bronze" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No invoices found</p>
              <p className="text-sm">Create invoices from unbilled booking entries</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Invoice #</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Property</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Period</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Reservations</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Volume</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Amount Due</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Due Date</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{invoice.invoice_number}</code>
                      </td>
                      <td className="py-3 px-4 font-medium">{invoice.property_name}</td>
                      <td className="py-3 px-4">{invoice.billing_period}</td>
                      <td className="py-3 px-4">{invoice.total_reservations}</td>
                      <td className="py-3 px-4">{formatCurrency(invoice.total_booking_volume)}</td>
                      <td className="py-3 px-4 font-semibold text-bronze">{formatCurrency(invoice.amount_due)}</td>
                      <td className="py-3 px-4">
                        <Badge className={
                          invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                          invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm">{formatDate(invoice.due_date)}</td>
                      <td className="py-3 px-4">
                        {invoice.status === 'unpaid' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => markAsPaid(invoice.id)}
                            className="text-green-600 hover:text-green-700"
                            data-testid={`mark-paid-${invoice.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Mark Paid
                          </Button>
                        )}
                        {invoice.status === 'paid' && (
                          <span className="text-sm text-slate-400">Paid {formatDate(invoice.paid_at)}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>
              Create an invoice from unbilled booking entries for a property.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Property</label>
              <Select 
                value={createData.property_id} 
                onValueChange={(val) => setCreateData({...createData, property_id: val})}
              >
                <SelectTrigger data-testid="select-property">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p, idx) => (
                    <SelectItem key={idx} value={p.property_id}>
                      {p.property_name} ({formatCurrency(p.unbilled_fees)} unbilled)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Billing Month</label>
              <Input 
                type="month" 
                value={createData.month}
                onChange={(e) => setCreateData({...createData, month: e.target.value})}
                max={getCurrentMonth()}
                data-testid="select-month"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Notes (optional)</label>
              <Input 
                placeholder="Add any notes for this invoice..."
                value={createData.notes}
                onChange={(e) => setCreateData({...createData, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button 
              onClick={createInvoice} 
              disabled={creating}
              className="bg-bronze hover:bg-amber-600 text-white"
              data-testid="confirm-create-invoice"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Property Statement Modal
function PropertyStatementModal({ property, onClose }) {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    if (property?.property_id) {
      fetchStatement();
    }
  }, [property, selectedMonth]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/revenue/statement/${property.property_id}/${selectedMonth}`,
        { headers: getAdminAuthHeaders() }
      );
      setStatement(response.data);
    } catch (error) {
      console.error("Failed to fetch statement:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Dialog open={!!property} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-bronze" />
            Property Statement: {property?.property_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <div className="flex items-center gap-4 mb-6">
            <label className="text-sm text-slate-600">Statement Month:</label>
            <Input 
              type="month" 
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-48"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-bronze" />
            </div>
          ) : (
            <>
              {/* Statement Summary */}
              <div className="bg-slate-50 rounded-lg p-6 mb-6">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-sm text-slate-600">Reservations</p>
                    <p className="text-2xl font-bold text-midnight">{statement?.total_reservations || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Booking Volume</p>
                    <p className="text-2xl font-bold text-midnight">{formatCurrency(statement?.total_booking_volume)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Fee Rate</p>
                    <p className="text-2xl font-bold text-midnight">{statement?.platform_fee_percent || 2}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-bronze">Platform Fees</p>
                    <p className="text-2xl font-bold text-bronze">{formatCurrency(statement?.total_platform_fees)}</p>
                  </div>
                </div>
              </div>

              {/* Entries Table */}
              {statement?.entries?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Guest</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Dates</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Amount</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Fee</th>
                        <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.entries.map((entry, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2 px-3">{entry.guest_name}</td>
                          <td className="py-2 px-3 text-xs">{entry.checkin_date} - {entry.checkout_date}</td>
                          <td className="py-2 px-3">{formatCurrency(entry.booking_total)}</td>
                          <td className="py-2 px-3 text-bronze font-medium">{formatCurrency(entry.platform_fee_amount)}</td>
                          <td className="py-2 px-3">
                            <Badge className={
                              entry.billing_status === 'paid' ? 'bg-green-100 text-green-700' :
                              entry.billing_status === 'invoiced' ? 'bg-blue-100 text-blue-700' :
                              'bg-amber-100 text-amber-700'
                            }>
                              {entry.billing_status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">No bookings for this month</p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function RevenueManagement() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [connectedProperties, setConnectedProperties] = useState([]);
  const [syncForm, setSyncForm] = useState({ license_key: "", start_date: "", end_date: "" });
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchConnectedProperties();
  }, []);

  const fetchConnectedProperties = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue/cloudbeds-properties`, {
        headers: getAdminAuthHeaders()
      });
      setConnectedProperties(response.data.properties || []);
    } catch (error) {
      console.error("Failed to fetch connected properties:", error);
    }
  };

  const syncReservations = async () => {
    if (!syncForm.license_key) {
      toast.error("Please select a property");
      return;
    }
    
    setSyncing(true);
    setSyncResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/revenue/sync-cloudbeds`, syncForm, {
        headers: getAdminAuthHeaders()
      });
      setSyncResult(response.data);
      toast.success(`Synced ${response.data.synced} reservations!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync reservations");
      setSyncResult({ error: error.response?.data?.detail || "Failed to sync" });
    } finally {
      setSyncing(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="revenue-title">
              Revenue & Billing
            </h1>
            <p className="text-slate-600">Track booking commissions and manage property invoices (2% platform fee)</p>
          </div>
          <Button 
            onClick={() => setShowSyncModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="sync-cloudbeds-btn"
          >
            <CloudDownload className="w-4 h-4 mr-2" />
            Sync Past Reservations
          </Button>
        </div>

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-white border border-slate-200" data-testid="revenue-tabs">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-bronze data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="ledger" className="data-[state=active]:bg-bronze data-[state=active]:text-white">
              <Receipt className="w-4 h-4 mr-2" />
              Booking Ledger
            </TabsTrigger>
            <TabsTrigger value="properties" className="data-[state=active]:bg-bronze data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Properties
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-bronze data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Invoices
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab />
          </TabsContent>

          <TabsContent value="ledger">
            <LedgerTab />
          </TabsContent>

          <TabsContent value="properties">
            <PropertiesTab onSelectProperty={setSelectedProperty} />
          </TabsContent>

          <TabsContent value="invoices">
            <InvoicesTab />
          </TabsContent>
        </Tabs>

        {/* Property Statement Modal */}
        <PropertyStatementModal 
          property={selectedProperty} 
          onClose={() => setSelectedProperty(null)} 
        />

        {/* Sync Past Reservations Modal */}
        <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Sync Past Reservations from Cloudbeds
              </DialogTitle>
              <DialogDescription>
                Fetch historical reservations from Cloudbeds and add them to the booking ledger for billing.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Select Property</label>
                <Select 
                  value={syncForm.license_key} 
                  onValueChange={(val) => setSyncForm({...syncForm, license_key: val})}
                >
                  <SelectTrigger data-testid="sync-property-select">
                    <SelectValue placeholder="Select a Cloudbeds-connected property" />
                  </SelectTrigger>
                  <SelectContent>
                    {connectedProperties.length === 0 ? (
                      <SelectItem value="none" disabled>No properties connected to Cloudbeds</SelectItem>
                    ) : (
                      connectedProperties.map((prop, idx) => (
                        <SelectItem key={idx} value={prop.license_key}>
                          {prop.cloudbeds_property_name || prop.woocommerce_url} ({prop.customer_email})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Start Date</label>
                  <Input 
                    type="date" 
                    value={syncForm.start_date}
                    onChange={(e) => setSyncForm({...syncForm, start_date: e.target.value})}
                    placeholder="Leave empty for last 90 days"
                  />
                  <p className="text-xs text-slate-500">Default: 90 days ago</p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">End Date</label>
                  <Input 
                    type="date" 
                    value={syncForm.end_date}
                    onChange={(e) => setSyncForm({...syncForm, end_date: e.target.value})}
                    placeholder="Leave empty for today"
                  />
                  <p className="text-xs text-slate-500">Default: Today</p>
                </div>
              </div>

              {syncResult && (
                <div className={`p-4 rounded-lg ${syncResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  {syncResult.error ? (
                    <p className="text-red-700">{syncResult.error}</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="font-medium text-green-700">Sync Complete!</p>
                      <div className="text-sm text-green-600 space-y-1">
                        <p>✓ Synced: {syncResult.synced} reservations</p>
                        <p>○ Skipped (already exists): {syncResult.skipped}</p>
                        <p>📅 Date range: {syncResult.date_range?.start} to {syncResult.date_range?.end}</p>
                      </div>
                      {syncResult.errors?.length > 0 && (
                        <div className="mt-2 text-amber-600 text-sm">
                          Warnings: {syncResult.errors.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSyncModal(false)}>
                {syncResult ? "Done" : "Cancel"}
              </Button>
              {!syncResult && (
                <Button 
                  onClick={syncReservations}
                  disabled={syncing || !syncForm.license_key}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="confirm-sync-btn"
                >
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-2" />}
                  Sync Reservations
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
